import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { Lock, Loader2, LogOut } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAuthContext = createContext(null);
export const useAdminAuth = () => useContext(AdminAuthContext);

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

const AdminGuard = ({ children }) => {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/auth/me`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setUser)
      .catch(() => setUser(false));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(formatApiErrorDetail(data.detail));
        return;
      }
      setUser(data);
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = useCallback(async () => {
    await fetch(`${API_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    setUser(false);
    setPassword('');
  }, []);

  const contextValue = useMemo(() => ({ user, logout: handleLogout }), [user, handleLogout]);

  if (user === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#7B1E2B]" />
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="min-h-screen bg-[#001B2A] flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-sm p-8">
          <div className="w-14 h-14 bg-[#E6D7B3] rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="h-7 w-7 text-[#001B2A]" />
          </div>
          <h1 className="text-2xl font-serif font-bold text-[#001B2A] text-center mb-1">Owner Access</h1>
          <p className="text-sm text-gray-500 text-center mb-6">Sign in to manage your store</p>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              data-testid="admin-login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7B1E2B] focus:border-transparent"
            />
            <input
              data-testid="admin-login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7B1E2B] focus:border-transparent"
            />
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2.5">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 bg-[#7B1E2B] text-white py-3 rounded-md font-semibold hover:bg-[#001B2A] transition-colors disabled:opacity-60"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <AdminAuthContext.Provider value={contextValue}>
      <div className="bg-[#7B1E2B] text-white text-sm px-4 py-2 flex items-center justify-between">
        <span>Signed in as {user.email}</span>
        <button
          data-testid="admin-logout-button"
          onClick={handleLogout}
          className="flex items-center gap-1.5 hover:underline"
        >
          <LogOut className="h-4 w-4" /> Log out
        </button>
      </div>
      {children}
    </AdminAuthContext.Provider>
  );
};

export default AdminGuard;
